import {
  useCallback, useState,
} from 'react';
import { type MapInfo } from '~/_shared/types/map';
import { type PerSpreadsheetMatchupData } from '~/_shared/types/spreadsheetData/matchupData';
import { filterAddedSpreadsheetColumns } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { type SpreadsheetMapInfo } from '~/map/listing/item/mapListingItem';
import {
  guessMapIntersects, type MapIntersect,
} from '~/map/map.repository';
import {
  getSpreadsheetMatchup, type GetSpreadsheetMatchupDataRequestItem,
} from '~/spreadsheet/spreadsheet.repository';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import {
  getMapFromRealSpreadsheetId, getMapIdFromPrimarySpreadsheetId, getPrimarySpreadsheetIdFromMapId,
  getRealSpreadsheetIdFromMapId, getSpreadsheetIdFromMapId,
} from './matchupColumnsSection.helpers';

export const useMatchupColumns = () => {
  const clientId = useClientIdSelector();
  const [isLoading, setIsLoading] = useState(false);
  const [intersects, setIntersects] = useState<MapIntersect[]>([]);
  const [columnData, setColumnData] = useState<PerSpreadsheetMatchupData>();

  const fetchPreselectionColumns = useCallback((mapList: ReadonlyArray<{
    id: number;
    spreadSheets?: ReadonlyArray<{
      spreadsheetId: number;
      isPrimary: boolean;
    }>;
  }>, layeringMapInfo?: MapInfo | null) => {
    if (!clientId) {
      return;
    }

    const primarySpreadsheetIds = mapList.map((map) => {
      const spreadsheets = map.spreadSheets || [];
      const primarySpreadsheet = spreadsheets.filter((spreadsheet: SpreadsheetMapInfo) => spreadsheet.isPrimary);
      return primarySpreadsheet?.[0]?.spreadsheetId;
    });

    const requestMatchUpDataItems: GetSpreadsheetMatchupDataRequestItem[] = primarySpreadsheetIds.map((spreadsheetId, index) => (
      spreadsheetId && mapList[index] ? {
        spreadsheet_id: spreadsheetId,
        map_id: mapList[index].id,
      } : undefined)
    ).filter(notNullOrUndefined);

    if (layeringMapInfo) {
      const currentMapPrimarySpreadsheet = layeringMapInfo.spreadsheets.find(s => s.isPrimary);
      if (currentMapPrimarySpreadsheet) {
        primarySpreadsheetIds.push(currentMapPrimarySpreadsheet.spreadSheetId);
        requestMatchUpDataItems.push({
          spreadsheet_id: currentMapPrimarySpreadsheet.spreadSheetId,
          map_id: layeringMapInfo.id,
        });
      }
    }

    // we could not filter primarySpreadsheetIds out above, as we needed to keep the original order for requestMatchUpDataItems
    const finalPrimarySpreadsheetIds = primarySpreadsheetIds.filter(notNullOrUndefined);
    if (finalPrimarySpreadsheetIds.length === 0) {
      return;
    }

    setIsLoading(true);

    guessMapIntersects(clientId, [], finalPrimarySpreadsheetIds)
      .then((response) => {
        setIntersects(filterAddedSpreadsheetColumns(response.list));
      })
      .finally(() => {
        getSpreadsheetMatchup(clientId, requestMatchUpDataItems)
          .then((response) => {
            setColumnData(response.data);
          })
          .finally(() => {
            setIsLoading(false);
          });
      });
  }, [clientId]);

  return {
    isLoading, intersects, columnData, getMapIdFromPrimarySpreadsheetId, getMapFromRealSpreadsheetId,
    fetchPreselectionColumns, getSpreadsheetIdFromMapId, getRealSpreadsheetIdFromMapId, getPrimarySpreadsheetIdFromMapId,
  };
};
